import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Ticket, 
  Monitor, 
  FileText, 
  Clock,
  CheckCircle2,
  Activity,
  Users,
  Plus,
  ChevronRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { itSupportData } from '../../data/itSupportData';
import { PriorityBadge, StatusBadge } from '../shared/Badges';

const OverviewTab = () => {
  const [period, setPeriod] = useState('week');
  const { stats, recentTickets } = itSupportData;

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Ticket className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-3xl font-semibold text-gray-900">{stats.openTickets}</h3>
              <p className="text-sm text-gray-500">Open Tickets</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />
              +12% from last week
            </span>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-3xl font-semibold text-gray-900">{stats.resolvedToday}</h3>
              <p className="text-sm text-gray-500">Resolved Today</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />
              +8% from last week
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-yellow-50 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-3xl font-semibold text-gray-900">{stats.avgResponseTime}</h3>
              <p className="text-sm text-gray-500">Avg Response Time</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />
              +5% from last week
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <Monitor className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-3xl font-semibold text-gray-900">{stats.activeAssets}</h3>
              <p className="text-sm text-gray-500">Active Assets</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-green-600 flex items-center gap-1">
              <ChevronRight className="w-4 h-4" />
              +3% from last week
            </span>
          </div>
        </div>
      </div>

      {/* Analytics and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket Analytics Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-semibold text-gray-900">Ticket Analytics</h2>
            <select 
              className="text-sm border-gray-200 rounded-lg px-3 py-2"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.ticketTrends}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="tickets"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentTickets.slice(0, 5).map((activity, index) => (
              <div key={index} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex gap-3">
                  <div className={`p-2 rounded-lg ${
                    activity.status === 'resolved' ? 'bg-green-50' : 
                    activity.status === 'open' ? 'bg-blue-50' : 
                    'bg-yellow-50'
                  }`}>
                    <Activity className={`w-4 h-4 ${
                      activity.status === 'resolved' ? 'text-green-600' :
                      activity.status === 'open' ? 'text-blue-600' : 
                      'text-yellow-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(activity.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link 
          to="/it-support/tickets"
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Ticket className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Ticket System</h3>
              <p className="text-sm text-gray-500">Manage support tickets</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
          </div>
        </Link>

        <Link 
          to="/it-support/assets"
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-lg">
              <Monitor className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">Asset Management</h3>
              <p className="text-sm text-gray-500">Track IT assets and inventory</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 transition-colors" />
          </div>
        </Link>

        <Link 
          to="/it-support/docs"
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">Documentation</h3>
              <p className="text-sm text-gray-500">Access IT procedures and guides</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-600 transition-colors" />
          </div>
        </Link>
      </div>

      {/* Recent Tickets List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="font-semibold text-gray-900">Recent Tickets</h2>
          <Link 
            to="/it-support/tickets"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {recentTickets.slice(0, 5).map((ticket) => (
            <div key={ticket.id} className="p-4 hover:bg-gray-50 transition-colors group">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`p-2 rounded-lg shrink-0 ${
                    ticket.status === 'resolved' ? 'bg-green-50' : 
                    ticket.status === 'open' ? 'bg-blue-50' : 
                    'bg-yellow-50'
                  }`}>
                    <Activity className={`w-4 h-4 ${
                      ticket.status === 'resolved' ? 'text-green-600' :
                      ticket.status === 'open' ? 'text-blue-600' : 
                      'text-yellow-600'
                    }`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{ticket.title}</p>
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                      <span className="truncate">{ticket.requester}</span>
                      <span>•</span>
                      <span className="truncate">{ticket.location}</span>
                      <span>•</span>
                      <span className="whitespace-nowrap">{new Date(ticket.createdAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <PriorityBadge priority={ticket.priority} />
                  <StatusBadge status={ticket.status} />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    <span>{ticket.assignedTo || 'Unassigned'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span>{ticket.category}</span>
                  </div>
                </div>
                <Link
                  to={`/it-support/tickets/${ticket.id}`}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab; 